import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  type DeviceGroupAttribute,
  type GraphPath,
  type Layout,
  layouts,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { saveUserDefinedLayout } from 'components/Graph/commands/saveUserDefinedLayout';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, memo, useEffect, useState } from 'react';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';
import { useApi } from 'utils/request/useApi';
import { type GraphState, isTopologyGraphMetadata } from '../GraphState';
import {
  type GraphLayoutChangeEvent,
  GraphLayoutItem,
} from './GraphLayoutItem';

const selectPartialLayout: (s: GraphState) => boolean = createScopedSelector(
  () =>
    createSelector(
      prop('graphLayoutDialog'),
      (graphLayoutDialog) =>
        !!(graphLayoutDialog?.nodeSns && graphLayoutDialog?.nodeSns.length > 0),
    ),
);

interface Props {
  activeLayout: Layout | null;
  groupBy: DeviceGroupAttribute;
  layoutItemClassName?: string;
  path: GraphPath;
  onChange: (event: GraphLayoutChangeEvent) => void;
  onSaveUserDefined?: () => void;
  showSaveUserDefined?: boolean;
}

const BareGraphLayout: FC<Props> = (props) => {
  const msg = useLocale();
  const apiRequest = useApi().request;
  const [showUserDefined, setShowUserDefined] = useState<boolean>(
    !!props.showSaveUserDefined,
  );
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    partialLayout: selectPartialLayout,
    paths: (s) =>
      isTopologyGraphMetadata(s.graphMetadata) ? s.graphMetadata.paths : null,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: initial default positions load
  useEffect(() => {
    if (showUserDefined) {
      // showing user defined option because there are unsaved changes,
      // no need to fetch anything
      return;
    }

    /**
     * Load site info if site has already saved positions.
     * If yes, we will show 'userDefined' option
     */
    const loadDefaultPositions = async () => {
      const response = await apiRequest({
        method: 'GET',
        url: `/graphs/default-positions/${encodeURIComponent(props.path)}?groupBy=${encodeURIComponent(props.groupBy)}`,
      });

      setShowUserDefined(Object.keys(response?.positions ?? {}).length > 0);
    };

    loadDefaultPositions();
  }, []);

  const handleSaveUserDefined = useStableCallback(() => {
    ctxProps.dispatch(saveUserDefinedLayout, { path: props.path });
    props.onSaveUserDefined?.();
  });

  const pathInfo = ctxProps.paths?.[props.path];

  return (
    <>
      {layouts
        // Don't show 'userDefined' layout if node positions haven't changed
        .filter(
          (layout) =>
            layout !== 'userDefined' ||
            (showUserDefined && !ctxProps.partialLayout),
        )
        .map((layout) => (
          <GraphLayoutItem
            checked={layout === props.activeLayout}
            description={msg.canvasGraph.graphLayout[layout].description}
            key={layout}
            layout={layout}
            layoutItemClassName={props.layoutItemClassName}
            onChange={props.onChange}
            {...(layout === 'userDefined' &&
              props.showSaveUserDefined && { onSave: handleSaveUserDefined })}
            partialLayout={ctxProps.partialLayout}
            pathInfo={pathInfo}
            title={msg.canvasGraph.graphLayout[layout].title}
            unsavedChanges={props.showSaveUserDefined}
          />
        ))}
    </>
  );
};

export const GraphLayout = memo(BareGraphLayout);
